import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { GenericItemListTranslationService } from './generic-item-list-translation.service';
import { GenericItemListTranslations, GenericItemListTranslationInput } from './generic-item-list-translations.interface';
import { focusNearestElement } from 'app/shared/utils/focusHelper';

export interface GenericItemListItemContext<T> {
  $implicit: T;
  index: number;
  total: number;
  readonly: boolean;
}

@Component({
  selector: 'app-generic-item-list',
  templateUrl: './generic-item-list.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class GenericItemListComponent<T = any> implements OnInit, OnChanges {
  @Input() idPrefix: string = 'generic-item-list';
  @Input() data: T[] = [];
  @Input() readonly: boolean = false;
  @Input() oddClass?: string;
  @Input() evenClass?: string;
  @Input() alwaysShowPlaceholder: boolean = false;
  @Input() translations?: GenericItemListTranslationInput;
  @Input() translationParams: Record<string, string | number> = {};

  @Output() append = new EventEmitter<void>();
  @Output() remove = new EventEmitter<{ item: T; index: number }>();

  @ContentChild(TemplateRef) itemTemplate: TemplateRef<GenericItemListItemContext<T>>;

  public resolvedTranslations: GenericItemListTranslations;

  constructor(private translationService: GenericItemListTranslationService) {
    this.resolvedTranslations = this.translationService.getResolvedTranslations();
  }

  ngOnInit(): void {
    this.resolveTranslations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['translations'] || changes['translationParams']) && !changes['translations']?.firstChange) {
      this.resolveTranslations();
    }
  }

  private resolveTranslations(): void {
    this.resolvedTranslations = this.applyTranslationParams(
      this.translationService.getResolvedTranslations(this.translations)
    );
  }

  private applyTranslationParams(translations: GenericItemListTranslations): GenericItemListTranslations {
    if (!this.translationParams || Object.keys(this.translationParams).length === 0) {
      return translations;
    }
    return this.interpolateObject(translations) as GenericItemListTranslations;
  }

  private interpolateObject(obj: any): any {
    if (typeof obj === 'string') {
      return obj.replace(/\{(\w+)\}/g, (_, key) => {
        const val = this.translationParams[key];
        return val !== undefined ? String(val) : `{${key}}`;
      });
    }
    if (typeof obj === 'object' && obj !== null) {
      const result: any = {};
      for (const key of Object.keys(obj)) {
        result[key] = this.interpolateObject(obj[key]);
      }
      return result;
    }
    return obj;
  }

  public getItemContext(item: T, index: number): GenericItemListItemContext<T> {
    return {
      $implicit: item,
      index,
      total: this.data.length,
      readonly: this.readonly,
    };
  }

  public appendItem(): void {
    this.append.emit();
    focusNearestElement(`#${this.idPrefix}-list > li:last-child .uba-item-list__body`, {
      delay: 50,
      preventScroll: false,
    });
  }

  public removeItem(index: number): void {
    const item = this.data[index];
    const lengthBefore = this.data.length;
    this.remove.emit({ item, index });

    // Consumer mutates the array synchronously inside the remove handler;
    // compare lengths to detect whether the item was actually removed.
    if (this.data.length < lengthBefore) {
      if (lengthBefore > 1) {
        const target = Math.min(index, lengthBefore - 2);
        focusNearestElement(`#${this.idPrefix}-list > li:nth-child(${target + 1}) .uba-item-list__body`, {
          delay: 50,
          preventScroll: false,
        });
      } else {
        focusNearestElement(`[aria-controls="${this.idPrefix}-list"]`, {
          delay: 50,
          preventScroll: false,
        });
      }
    }
  }

  public t(key: string, params?: { [key: string]: string | number }): string {
    return this.translationService.getFromResolved(key, this.resolvedTranslations, params);
  }
}
